import { collection, query, where, getDocs } from "firebase/firestore";

/**
 * Utilities for company detection, sequential estimate / PO numbering,
 * and concurrency collision verification.
 *
 * Sequence formatting guidelines:
 * - Teltech: 4-digit sequence (seed format 1200) -> detects newest in sequence + 1
 * - Longarm Technologies: '26-XXX' format (seed format 26-001) -> detects newest in sequence + 1
 * - Royal Oaks: 4-digit sequence (seed format 2000) -> detects newest in sequence + 1
 * - Default / Other: 'EST-YYYY-XXX' -> detects newest in sequence + 1
 */

/**
 * Detects the normalized company key from company logo, explicit company name, or client name.
 * @param {Object} params
 * @param {string} [params.logoUrl]
 * @param {string} [params.companyName]
 * @param {string} [params.clientName]
 * @param {Array} [params.companyLogos]
 * @returns {"teltech" | "longarm" | "royaloaks" | "default"}
 */
export function detectCompanyKey({ logoUrl, companyName, clientName, companyLogos = [] } = {}) {
  let resolvedName = "";

  if (companyName && typeof companyName === "string") {
    resolvedName = companyName;
  } else if (logoUrl && Array.isArray(companyLogos)) {
    const matched = companyLogos.find((l) => l.url === logoUrl);
    if (matched && matched.name) {
      resolvedName = matched.name;
    }
  }

  // Check resolved issuing company first
  if (resolvedName) {
    const lower = resolvedName.toLowerCase();
    if (lower.includes("teltech")) return "teltech";
    if (lower.includes("longarm")) return "longarm";
    if (lower.includes("royal oak")) return "royaloaks";
  }

  // Fallback to checking clientName if issuing company is not set or general
  if (clientName && typeof clientName === "string") {
    const lowerClient = clientName.toLowerCase();
    if (lowerClient.includes("teltech")) return "teltech";
    if (lowerClient.includes("longarm")) return "longarm";
    if (lowerClient.includes("royal oak")) return "royaloaks";
  }

  return "default";
}

/**
 * Resolves the user-friendly company name based on the detected key or logo.
 * @param {string} companyKey
 * @param {Array} [companyLogos]
 * @param {string} [logoUrl]
 * @returns {string}
 */
export function getCompanyDisplayName(companyKey, companyLogos = [], logoUrl = "") {
  if (logoUrl && Array.isArray(companyLogos)) {
    const matched = companyLogos.find((l) => l.url === logoUrl);
    if (matched?.name) return matched.name;
  }

  switch (companyKey) {
    case "teltech":
      return "Teltech";
    case "longarm":
      return "Longarm Technologies";
    case "royaloaks":
      return "Royal Oaks";
    default:
      return "";
  }
}

/**
 * Generates the next sequential estimate / PO number dynamically based on
 * the newest number in sequence found in existing records.
 * Seed numbers (1200, 2000, 26-001, EST-2026-001) are strictly formatting baselines
 * when no records currently exist.
 *
 * @param {Object} params
 * @param {string} params.companyKey - "teltech" | "longarm" | "royaloaks" | "default"
 * @param {Array} params.existingPos - List of existing PO documents
 * @param {Array} [params.companyLogos] - List of configured company logo objects
 * @returns {string}
 */
export function generateNextCompanyPoNumber({ companyKey, existingPos = [], companyLogos = [] }) {
  const safePos = Array.isArray(existingPos) ? existingPos : [];

  if (companyKey === "teltech") {
    let maxNum = 0;
    safePos.forEach((po) => {
      const poNumStr = (po.poNumber || "").trim();
      const belongs =
        (po.companyName && /teltech/i.test(po.companyName)) ||
        (po.clientName && /teltech/i.test(po.clientName)) ||
        (po.logoUrl && companyLogos.some((l) => l.url === po.logoUrl && /teltech/i.test(l.name || ""))) ||
        /^12\d{2,}$/.test(poNumStr);

      if (belongs) {
        const match = poNumStr.match(/(\d+)/);
        if (match) {
          const val = parseInt(match[1], 10);
          if (Number.isFinite(val) && val > maxNum) {
            maxNum = val;
          }
        }
      }
    });

    // If no records exist, use company baseline format 1200; otherwise increment newest
    return maxNum === 0 ? "1200" : String(maxNum + 1);
  }

  if (companyKey === "longarm") {
    let maxSuffix = 0;
    let padLength = 3;
    safePos.forEach((po) => {
      const poNumStr = (po.poNumber || "").trim();
      const belongs =
        (po.companyName && /longarm/i.test(po.companyName)) ||
        (po.clientName && /longarm/i.test(po.clientName)) ||
        (po.logoUrl && companyLogos.some((l) => l.url === po.logoUrl && /longarm/i.test(l.name || ""))) ||
        /^26-\d+$/i.test(poNumStr);

      if (belongs) {
        const match = poNumStr.match(/^26-(\d+)/i);
        if (match) {
          const digits = match[1];
          const val = parseInt(digits, 10);
          if (Number.isFinite(val) && val > maxSuffix) {
            maxSuffix = val;
            if (digits.length > padLength) padLength = digits.length;
          }
        }
      }
    });

    // If no records exist, use company baseline format 26-001; otherwise increment newest
    const nextVal = maxSuffix === 0 ? 1 : maxSuffix + 1;
    return `26-${String(nextVal).padStart(padLength, "0")}`;
  }

  if (companyKey === "royaloaks") {
    let maxNum = 0;
    safePos.forEach((po) => {
      const poNumStr = (po.poNumber || "").trim();
      const belongs =
        (po.companyName && /royal\s*oak/i.test(po.companyName)) ||
        (po.clientName && /royal\s*oak/i.test(po.clientName)) ||
        (po.logoUrl && companyLogos.some((l) => l.url === po.logoUrl && /royal\s*oak/i.test(l.name || ""))) ||
        /^20\d{2,}$/.test(poNumStr);

      if (belongs) {
        const match = poNumStr.match(/(\d+)/);
        if (match) {
          const val = parseInt(match[1], 10);
          if (Number.isFinite(val) && val > maxNum) {
            maxNum = val;
          }
        }
      }
    });

    // If no records exist, use company baseline format 2000; otherwise increment newest
    return maxNum === 0 ? "2000" : String(maxNum + 1);
  }

  // Default sequence fallback: EST-YYYY-XXX
  const currentYear = new Date().getFullYear();
  let maxSeq = 0;
  let detectedYear = currentYear;

  safePos.forEach((po) => {
    const poNumStr = (po.poNumber || "").trim();
    const match = poNumStr.match(/EST-(\d{4})-(\d+)/i);
    if (match) {
      const yr = parseInt(match[1], 10);
      const val = parseInt(match[2], 10);
      if (yr === currentYear && Number.isFinite(val) && val > maxSeq) {
        maxSeq = val;
        detectedYear = yr;
      }
    }
  });

  const nextVal = maxSeq + 1;
  return `EST-${detectedYear}-${String(nextVal).padStart(3, "0")}`;
}

/**
 * Checks if a given PO number already exists in Firestore.
 * @param {import("firebase/firestore").Firestore} db
 * @param {string} poNumber
 * @returns {Promise<boolean>}
 */
export async function checkPoNumberExists(db, poNumber) {
  if (!db || !poNumber || typeof poNumber !== "string" || !poNumber.trim()) return false;
  try {
    const q = query(collection(db, "pos"), where("poNumber", "==", poNumber.trim()));
    const snap = await getDocs(q);
    return !snap.empty;
  } catch (err) {
    console.error("Error checking PO number uniqueness:", err);
    return false;
  }
}

/**
 * Queries Firestore live to determine the guaranteed newest available sequence number,
 * eliminating race conditions between concurrent users creating estimates.
 *
 * @param {Object} params
 * @param {import("firebase/firestore").Firestore} params.db
 * @param {string} params.companyKey
 * @param {Array} [params.companyLogos]
 * @param {Array} [params.existingPos]
 * @returns {Promise<string>}
 */
export async function getGuaranteedNextPoNumber({ db, companyKey, companyLogos = [], existingPos = [] }) {
  let candidate = generateNextCompanyPoNumber({ companyKey, existingPos, companyLogos });
  if (!db) return candidate;

  try {
    const snap = await getDocs(collection(db, "pos"));
    const dbPos = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    candidate = generateNextCompanyPoNumber({ companyKey, existingPos: dbPos, companyLogos });

    let exists = dbPos.some((p) => (p.poNumber || "").trim().toLowerCase() === candidate.trim().toLowerCase());
    let safetyCounter = 0;
    while (exists && safetyCounter < 50) {
      safetyCounter++;
      dbPos.push({ poNumber: candidate, companyName: getCompanyDisplayName(companyKey, companyLogos) });
      candidate = generateNextCompanyPoNumber({ companyKey, existingPos: dbPos, companyLogos });
      exists = dbPos.some((p) => (p.poNumber || "").trim().toLowerCase() === candidate.trim().toLowerCase());
    }
  } catch (err) {
    console.error("Failed to query live Firestore for next PO sequence:", err);
  }

  return candidate;
}

/**
 * Checks if an entered PO number appears to be customized by the user
 * rather than matching an automated pattern.
 * @param {string} poNumber
 * @returns {boolean}
 */
export function isStandardGeneratedPoNumber(poNumber) {
  if (!poNumber || typeof poNumber !== "string") return false;
  const trimmed = poNumber.trim();
  if (/^EST-\d{4}-\d+$/i.test(trimmed)) return true;
  if (/^26-\d+$/i.test(trimmed)) return true;
  if (/^\d{4,}$/.test(trimmed)) return true;
  return false;
}
